// @ts-check
import { Content, ContextualHelp, Flex, Heading } from '@adobe/react-spectrum';
import React, { useContext, useEffect } from 'react';
import { useErrorBoundary, withErrorBoundary } from 'react-error-boundary';

import usePublisherApps from '../../../../../actions/myxchngApi/selectors/usePublisherApps';
import { DetailHeading } from '../../../../../components/DetailHeading';
import { NO_DETAILS_MESSAGE } from '../../../../../components/TraderInformation';
import { ContainerContext } from '../../../../../utils';
import getAppIcon from '../../../utils/GetAppIcon';
import ErrorFallback from './ErrorFallback';
import PublisherAppsTabContent from './PublisherAppsTabContent';
import PublisherAppsTabs from './PublisherAppsTabs';

/**
 * Publisher Profile apps component
 * @param {object} props
 * @param {function} props.setPublisherLogo - function to set the publisher logo
 * @param {'cc'|'ec'|'dc'} props.type - type of the publisher
 * @param {boolean} props.showHiddenListingsDisclaimer - indicates if the hidden listings disclaimer should be shown
 */
const PublisherApps = ({ setPublisherLogo, type, showHiddenListingsDisclaimer }) => {
  const { ims } = useContext(ContainerContext);
  const { showBoundary } = useErrorBoundary();
  const { applicationsQuery, solutionsQuery, isLoading } = usePublisherApps({
    type,
    refetchOnMount: true,
    keepPreviousData: true,
  });

  const error = solutionsQuery.error && applicationsQuery.error;
  const totalSolutions = solutionsQuery.data?.totalCount;
  const totalApplications = applicationsQuery.data?.totalCount;
  const showTabs = Boolean(totalSolutions && totalApplications);

  // Some EC/DC publishers don't have a logo, so we need to get it from the first app
  useEffect(() => {
    const firstApp = applicationsQuery.data?.cloudApps?.[0] ?? solutionsQuery.data?.cloudApps?.[0];
    if (firstApp && type !== 'cc' && setPublisherLogo) {
      const app = /** @type {import('@action-types/ecApp').AppCardDetails} */ (firstApp);
      const publisherLogo = getAppIcon(app?.assets);
      if (publisherLogo) {
        setPublisherLogo((currentPublisherLogo) => currentPublisherLogo || publisherLogo);
      }
    }
  }, [applicationsQuery.data?.cloudApps, setPublisherLogo, solutionsQuery.data?.cloudApps, type]);

  if (error) {
    const status = error.fetchResponse?.status;
    // CCES asked us to redirect to login on 429
    if (status === 429) {
      ims?.signIn();
      return null;
    }
    showBoundary(error);
    return null;
  }

  if (!totalApplications && !totalSolutions && !isLoading) {
    return null;
  }

  return (
    <Flex direction="column" alignItems="stretch">
      <Flex alignItems="center" gap="size-50">
        <DetailHeading>by this publisher</DetailHeading>
        {showHiddenListingsDisclaimer && (
          <ContextualHelp variant="info" aria-label="Publisher apps info">
            <Heading>Hidden listings</Heading>
            <Content>{NO_DETAILS_MESSAGE} Some of their listings might not be visible.</Content>
          </ContextualHelp>
        )}
      </Flex>
      {showTabs ? <PublisherAppsTabs /> : <PublisherAppsTabContent type={type} />}
    </Flex>
  );
};

export default withErrorBoundary(PublisherApps, {
  fallbackRender: ({ error }) => <ErrorFallback error={error} showTryAgainLink />,
  onError: console.error,
});
